import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Flag, RefreshCw, Plus, X, ChevronDown, ChevronUp, ToggleLeft, ToggleRight,
} from 'lucide-react';
import adminService from '../../services/adminService';
import AdminNav from './AdminNav';
import { cn } from '../../lib/utils';

const AdminFlags = () => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [emailInputs, setEmailInputs] = useState({}); // flagKey → string
  const [actionLoading, setActionLoading] = useState({});
  const [toast, setToast] = useState(null);
  const [newFlag, setNewFlag] = useState({ key: '', name: '', description: '' });
  const [showNewForm, setShowNewForm] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setFlags(await adminService.getAllFlags());
    } catch {
      showToast('Failed to load feature flags', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const withLoading = async (key, fn) => {
    setActionLoading(prev => ({ ...prev, [key]: true }));
    try { await fn(); } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleToggle = async (flag) => {
    await withLoading(`toggle-${flag.key}`, async () => {
      await adminService.toggleFlag(flag.key);
      showToast(`${flag.key} ${flag.enabled ? 'disabled' : 'enabled'}`);
      await load();
    });
  };

  const handleAddEmail = async (flag) => {
    const email = (emailInputs[flag.key] || '').trim().toLowerCase();
    if (!email) return;
    await withLoading(`email-${flag.key}`, async () => {
      try {
        await adminService.addEmailToFlag(flag.key, email);
        setEmailInputs(prev => ({ ...prev, [flag.key]: '' }));
        showToast(`${email} added to ${flag.key}`);
        await load();
      } catch (err) {
        showToast(err.response?.data?.error || 'Failed to add email', 'error');
      }
    });
  };

  const handleRemoveEmail = async (flag, email) => {
    await withLoading(`remove-${flag.key}-${email}`, async () => {
      await adminService.removeEmailFromFlag(flag.key, email);
      showToast(`${email} removed from ${flag.key}`);
      await load();
    });
  };

  const handleCreateFlag = async () => {
    if (!newFlag.key || !newFlag.name) return;
    await withLoading('create', async () => {
      try {
        await adminService.createFlag(newFlag);
        showToast(`Flag ${newFlag.key} created`);
        setNewFlag({ key: '', name: '', description: '' });
        setShowNewForm(false);
        await load();
      } catch (err) {
        showToast(err.response?.data?.error || 'Failed to create flag', 'error');
      }
    });
  };

  return (
    <div>
      <AdminNav />
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-in fade-in slide-in-from-top-2',
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
        )}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Feature Flags</h1>
          <p className="text-sm text-gray-500 mt-0.5">Control which features and users have access</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', loading && 'animate-spin')} />
            Refresh
          </Button>
          <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => setShowNewForm(v => !v)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New Flag
          </Button>
        </div>
      </div>

      {/* New flag form */}
      {showNewForm && (
        <Card className="border-cyan-200 bg-cyan-50/40">
          <CardContent className="pt-4 space-y-3">
            <p className="text-sm font-medium text-gray-700">Create new flag</p>
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="KEY (e.g. ADMIN_ENABLED)"
                value={newFlag.key}
                onChange={e => setNewFlag(p => ({ ...p, key: e.target.value.toUpperCase() }))}
                className="text-sm"
              />
              <Input
                placeholder="Display name"
                value={newFlag.name}
                onChange={e => setNewFlag(p => ({ ...p, name: e.target.value }))}
                className="text-sm"
              />
            </div>
            <Input
              placeholder="Description (optional)"
              value={newFlag.description}
              onChange={e => setNewFlag(p => ({ ...p, description: e.target.value }))}
              className="text-sm"
            />
            <div className="flex gap-2">
              <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700"
                onClick={handleCreateFlag}
                disabled={!newFlag.key || !newFlag.name || actionLoading.create}
              >
                {actionLoading.create ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : 'Create'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowNewForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Flags list */}
      {loading ? (
        <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Loading…</div>
      ) : flags.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-gray-400 text-sm">No feature flags found</div>
      ) : (
        <div className="space-y-3">
          {flags.map(flag => {
            const isExpanded = !!expanded[flag.key];
            const emails = flag.enabledForEmails || [];
            const users = flag.enabledForUsers || [];
            const toggling = actionLoading[`toggle-${flag.key}`];

            return (
              <Card key={flag.key}>
                {/* Flag row */}
                <div className="px-4 py-3 flex items-center gap-3">
                  <div className={cn(
                    'h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0',
                    flag.enabled ? 'bg-cyan-100' : 'bg-gray-100'
                  )}>
                    <Flag className={cn('h-4 w-4', flag.enabled ? 'text-cyan-600' : 'text-gray-400')} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-gray-900">{flag.key}</span>
                      {flag.enabled && (
                        <span className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-medium px-1.5 py-0.5">
                          Globally ON
                        </span>
                      )}
                    </div>
                    {flag.name && flag.name !== flag.key && (
                      <p className="text-xs text-gray-500">{flag.name}{flag.description ? `: ${flag.description}` : ''}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      {emails.length} email{emails.length !== 1 ? 's' : ''} · {users.length} user{users.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggle(flag)}
                      disabled={toggling}
                      className="focus:outline-none"
                      title={flag.enabled ? 'Disable globally' : 'Enable globally'}
                    >
                      {toggling ? (
                        <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
                      ) : flag.enabled ? (
                        <ToggleRight className="h-6 w-6 text-cyan-600" />
                      ) : (
                        <ToggleLeft className="h-6 w-6 text-gray-400" />
                      )}
                    </button>
                    <button
                      onClick={() => setExpanded(prev => ({ ...prev, [flag.key]: !isExpanded }))}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Expanded email management */}
                {isExpanded && (
                  <div className="border-t bg-gray-50/60 px-4 py-3 space-y-3">
                    {/* Add email */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add email address…"
                        value={emailInputs[flag.key] || ''}
                        onChange={e => setEmailInputs(prev => ({ ...prev, [flag.key]: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && handleAddEmail(flag)}
                        className="h-8 text-sm flex-1"
                      />
                      <Button
                        size="sm"
                        className="h-8 bg-cyan-600 hover:bg-cyan-700"
                        onClick={() => handleAddEmail(flag)}
                        disabled={!emailInputs[flag.key]?.trim() || actionLoading[`email-${flag.key}`]}
                      >
                        {actionLoading[`email-${flag.key}`]
                          ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                          : <Plus className="h-3.5 w-3.5" />}
                      </Button>
                    </div>

                    {/* Email list */}
                    {emails.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No emails added</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {emails.map(email => (
                          <span
                            key={email}
                            className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full px-2.5 py-1 text-xs text-gray-700"
                          >
                            {email}
                            <button
                              onClick={() => handleRemoveEmail(flag, email)}
                              disabled={actionLoading[`remove-${flag.key}-${email}`]}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              {actionLoading[`remove-${flag.key}-${email}`]
                                ? <RefreshCw className="h-3 w-3 animate-spin" />
                                : <X className="h-3 w-3" />}
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Named users */}
                    {users.length > 0 && (
                      <div>
                        <p className="text-[11px] text-gray-500 font-medium mb-1">Users by ID</p>
                        <div className="flex flex-wrap gap-1.5">
                          {users.map(u => {
                            const userId = u._id || u;
                            const label = u.email || u.username || userId;
                            return (
                              <span
                                key={userId}
                                className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full px-2.5 py-1 text-xs text-gray-700"
                              >
                                {label}
                                <button
                                  onClick={() => withLoading(`remove-user-${flag.key}-${userId}`, async () => {
                                    await adminService.removeUserFromFlag(flag.key, userId);
                                    await load();
                                  })}
                                  className="text-gray-400 hover:text-red-500 transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
    </div>
  );
};

export default AdminFlags;
